import { Info } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { TaskPerformanceSummary } from "@/lib/task-metrics";

function SummaryKpi({
  label,
  value,
  tooltip,
  variant = "default",
}: {
  label: string;
  value: string;
  tooltip: string;
  variant?: "default" | "danger" | "success" | "warning";
}) {
  return (
    <div className="rounded-lg border bg-muted/30 px-3 py-2 min-w-0">
      <div className="flex items-center gap-1">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">
          {label}
        </p>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="text-muted-foreground hover:text-foreground">
                <Info className="h-3 w-3 shrink-0" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <p
        className={cn(
          "text-lg font-semibold tabular-nums mt-0.5",
          variant === "danger" && "text-destructive",
          variant === "success" && "text-emerald-600",
          variant === "warning" && "text-amber-600",
        )}
      >
        {value}
      </p>
    </div>
  );
}

interface TaskPerformanceSummaryCardsProps {
  summary: TaskPerformanceSummary | null;
  loading?: boolean;
}

export function TaskPerformanceSummaryCards({
  summary,
  loading,
}: TaskPerformanceSummaryCardsProps) {
  if (loading || !summary) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="rounded-lg border bg-muted/20 h-[68px] animate-pulse" />
        ))}
      </div>
    );
  }

  const pendingLabel =
    summary.pending_subtasks > 0
      ? `${summary.pending} (${summary.pending_subtasks} subt.)`
      : String(summary.pending);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
      <SummaryKpi
        label="Conclusão"
        value={`${summary.completion_rate.toFixed(1)}%`}
        tooltip="Percentual de tarefas concluídas no período em relação ao total atribuído."
        variant="success"
      />
      <SummaryKpi
        label="Tempo médio"
        value={
          summary.avg_completion_days != null
            ? `${summary.avg_completion_days.toFixed(1)} dias`
            : "—"
        }
        tooltip="Média de dias entre criação e conclusão das tarefas finalizadas no período."
      />
      <SummaryKpi
        label="% Atraso"
        value={`${summary.overdue_rate_snapshot.toFixed(1)}%`}
        tooltip="Proporção de tarefas atrasadas entre as pendentes em aberto (snapshot atual)."
        variant={summary.overdue_rate_snapshot > 20 ? "danger" : "default"}
      />
      <SummaryKpi
        label="Pendentes"
        value={pendingLabel}
        tooltip="Tarefas pendentes com prazo futuro ou hoje ainda dentro do horário. Inclui subtarefas."
      />
      <SummaryKpi
        label="Risco médio"
        value={
          summary.avg_delay_risk_index != null
            ? `${summary.avg_delay_risk_index.toFixed(0)}/100`
            : "—"
        }
        tooltip="Índice composto (0–100) que combina atrasos atuais, prazos próximos, histórico de atraso e velocidade de conclusão."
        variant={
          (summary.avg_delay_risk_index ?? 0) >= 50
            ? "warning"
            : "default"
        }
      />
    </div>
  );
}
